// src/lib/completeness-score-calculator.ts
// PRIO 4 FIX: Calculate completeness score based on REAL user questions only

/**
 * Determines if a field is a real user question or a helper/duplicate field
 * 
 * EXCLUDED:
 * - *Exact fields (e.g., monthlyContactsExact)
 * - *Unknown fields (e.g., monthlyContactsUnknown)
 * - Duplicate mappings
 * - Internal helper fields
 */
export function isRealUserQuestion(questionId: string): boolean {
  // Exclude helper fields
  if (questionId.endsWith("Exact")) return false;
  if (questionId.endsWith("Unknown")) return false;
  
  // Exclude internal fields
  if (questionId.startsWith("_")) return false;
  
  // Exclude duplicate mappings (these are just transformations)
  const duplicateMappings = [
    "responseTime", // Mapped from raw
    "followUpDiscipline", // Mapped from raw
    "pipelineTransparency", // Mapped from raw
    "leadQuality", // Mapped from raw
  ];
  
  // Allow these if they're the ONLY version (not if *Exact exists)
  // This is handled by the Exact check above
  
  return true;
}

/**
 * Calculates completeness score based on real user questions only
 * 
 * @param answers - All answers from input
 * @param unknownFields - Fields marked as unknown
 * @returns Completeness score (0-100)
 */
export function calculateCompletenessScore(
  answers: Array<{ questionId: string; value: unknown }>,
  unknownFields: string[]
): number {
  // Filter to real user questions only
  const realQuestions = answers.filter(a => isRealUserQuestion(a.questionId));
  
  // Count answered questions (not null, not undefined, not "not-known")
  const answeredQuestions = realQuestions.filter(
    a => a.value !== null && a.value !== undefined && a.value !== "not-known"
  ).length;
  
  const totalQuestions = realQuestions.length;
  
  if (totalQuestions <= 0) return 0;
  
  // Calculate base score
  const answerScore = (answeredQuestions / totalQuestions) * 100;
  
  // Penalty for unknown fields (max 25%)
  const unknownPenalty = Math.min(unknownFields.length * 2.5, 25);
  
  return Math.max(0, Math.round(answerScore - unknownPenalty));
}

/**
 * Get statistics about question completeness
 */
export function getCompletenessStats(
  answers: Array<{ questionId: string; value: unknown }>
): {
  totalQuestions: number;
  answeredQuestions: number;
  helperFields: number;
  realQuestions: number;
} {
  const realQuestions = answers.filter(a => isRealUserQuestion(a.questionId));
  const helperFields = answers.length - realQuestions.length;
  
  const answeredQuestions = realQuestions.filter(
    a => a.value !== null && a.value !== undefined && a.value !== "not-known"
  ).length;
  
  return {
    totalQuestions: answers.length,
    answeredQuestions,
    helperFields,
    realQuestions: realQuestions.length,
  };
}
